/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { AlphabetLetter, WordItem } from '../types';

export const ENGLISH_ALPHABET: AlphabetLetter[] = [
  { letter: 'A', name: 'Ay', wordLocal: 'Apple', wordTranslation: 'Apple', wordImageEmoji: '🍎', phonetic: 'aah' },
  { letter: 'B', name: 'Bee', wordLocal: 'Ball', wordTranslation: 'Ball', wordImageEmoji: '⚽', phonetic: 'buh' },
  { letter: 'C', name: 'Cee', wordLocal: 'Cat', wordTranslation: 'Cat', wordImageEmoji: '🐱', phonetic: 'kuh' },
  { letter: 'D', name: 'Dee', wordLocal: 'Dog', wordTranslation: 'Dog', wordImageEmoji: '🐶', phonetic: 'duh' },
  { letter: 'E', name: 'Ee', wordLocal: 'Elephant', wordTranslation: 'Elephant', wordImageEmoji: '🐘', phonetic: 'eh' },
  { letter: 'F', name: 'Ef', wordLocal: 'Fish', wordTranslation: 'Fish', wordImageEmoji: '🐟', phonetic: 'fuh' },
  { letter: 'G', name: 'Gee', wordLocal: 'Grapes', wordTranslation: 'Grapes', wordImageEmoji: '🍇', phonetic: 'guh' },
  { letter: 'H', name: 'Aitch', wordLocal: 'Hat', wordTranslation: 'Hat', wordImageEmoji: '🎩', phonetic: 'huh' },
  { letter: 'I', name: 'Eye', wordLocal: 'Ice Cream', wordTranslation: 'Ice Cream', wordImageEmoji: '🍦', phonetic: 'ih' },
  { letter: 'J', name: 'Jay', wordLocal: 'Juice', wordTranslation: 'Juice', wordImageEmoji: '🥤', phonetic: 'juh' },
  { letter: 'K', name: 'Kay', wordLocal: 'Kite', wordTranslation: 'Kite', wordImageEmoji: '🪁', phonetic: 'kuh' },
  { letter: 'L', name: 'El', wordLocal: 'Lion', wordTranslation: 'Lion', wordImageEmoji: '🦁', phonetic: 'luh' },
  { letter: 'M', name: 'Em', wordLocal: 'Moon', wordTranslation: 'Moon', wordImageEmoji: '🌙', phonetic: 'muh' },
  { letter: 'N', name: 'En', wordLocal: 'Nest', wordTranslation: 'Nest', wordImageEmoji: '🪹', phonetic: 'nuh' },
  { letter: 'O', name: 'Oh', wordLocal: 'Orange', wordTranslation: 'Orange', wordImageEmoji: '🍊', phonetic: 'oh' },
  { letter: 'P', name: 'Pee', wordLocal: 'Parrot', wordTranslation: 'Parrot', wordImageEmoji: '🦜', phonetic: 'puh' },
  { letter: 'Q', name: 'Cue', wordLocal: 'Question Mark', wordTranslation: 'Question Mark', wordImageEmoji: '❓', phonetic: 'kwuh' },
  { letter: 'R', name: 'Ar', wordLocal: 'Rabbit', wordTranslation: 'Rabbit', wordImageEmoji: '🐰', phonetic: 'ruh' },
  { letter: 'S', name: 'Es', wordLocal: 'Sun', wordTranslation: 'Sun', wordImageEmoji: '☀️', phonetic: 'suh' },
  { letter: 'T', name: 'Tee', wordLocal: 'Tiger', wordTranslation: 'Tiger', wordImageEmoji: '🐯', phonetic: 'tuh' },
  { letter: 'U', name: 'You', wordLocal: 'Umbrella', wordTranslation: 'Umbrella', wordImageEmoji: '☂️', phonetic: 'uh' },
  { letter: 'V', name: 'Vee', wordLocal: 'Van', wordTranslation: 'Van', wordImageEmoji: '🚐', phonetic: 'vuh' },
  { letter: 'W', name: 'Double-U', wordLocal: 'Whale', wordTranslation: 'Whale', wordImageEmoji: '🐋', phonetic: 'wuh' },
  { letter: 'X', name: 'Ex', wordLocal: 'Xylophone', wordTranslation: 'Xylophone', wordImageEmoji: '🪘', phonetic: 'zuh' },
  { letter: 'Y', name: 'Wye', wordLocal: 'Yacht', wordTranslation: 'Yacht', wordImageEmoji: '⛵', phonetic: 'yuh' },
  { letter: 'Z', name: 'Zed', wordLocal: 'Zebra', wordTranslation: 'Zebra', wordImageEmoji: '🦓', phonetic: 'zuh' }
];

export const EXPLORER_ALPHABET_WORDS: Record<string, { word: string; icon: string; audioKey: string }[]> = {
  A: [
    { word: 'Apple', icon: '🍎', audioKey: 'apple' },
    { word: 'Airplane', icon: '✈️', audioKey: 'airplane' },
    { word: 'Arm', icon: '💪', audioKey: 'arm' },
    { word: 'Ant', icon: '🐜', audioKey: 'ant' },
    { word: 'Apron', icon: '🎽', audioKey: 'apron' }
  ],
  B: [
    { word: 'Ball', icon: '⚽', audioKey: 'ball' },
    { word: 'Banana', icon: '🍌', audioKey: 'banana' },
    { word: 'Book', icon: '📖', audioKey: 'book' },
    { word: 'Bird', icon: '🐦', audioKey: 'bird' },
    { word: 'Bus', icon: '🚌', audioKey: 'bus' }
  ],
  C: [
    { word: 'Cat', icon: '🐱', audioKey: 'cat' },
    { word: 'Car', icon: '🚗', audioKey: 'car' },
    { word: 'Cup', icon: '☕', audioKey: 'cup' },
    { word: 'Cake', icon: '🎂', audioKey: 'cake' },
    { word: 'Cow', icon: '🐮', audioKey: 'cow' }
  ],
  D: [
    { word: 'Dog', icon: '🐶', audioKey: 'dog' },
    { word: 'Doll', icon: '🪆', audioKey: 'doll' },
    { word: 'Door', icon: '🚪', audioKey: 'door' },
    { word: 'Duck', icon: '🦆', audioKey: 'duck' },
    { word: 'Drum', icon: '🥁', audioKey: 'drum' }
  ],
  E: [
    { word: 'Elephant', icon: '🐘', audioKey: 'elephant' },
    { word: 'Egg', icon: '🥚', audioKey: 'egg' },
    { word: 'Ear', icon: '👂', audioKey: 'ear' },
    { word: 'Eye', icon: '👁️', audioKey: 'eye' },
    { word: 'Engine', icon: '🚂', audioKey: 'engine' }
  ],
  F: [
    { word: 'Fish', icon: '🐟', audioKey: 'fish' },
    { word: 'Frog', icon: '🐸', audioKey: 'frog' },
    { word: 'Fan', icon: '🪭', audioKey: 'fan' },
    { word: 'Flower', icon: '🌸', audioKey: 'flower' },
    { word: 'Flag', icon: '🚩', audioKey: 'flag' }
  ],
  G: [
    { word: 'Grapes', icon: '🍇', audioKey: 'grapes' },
    { word: 'Goat', icon: '🐐', audioKey: 'goat' },
    { word: 'Girl', icon: '👧', audioKey: 'girl' },
    { word: 'Glass', icon: '🥛', audioKey: 'glass' },
    { word: 'Gift', icon: '🎁', audioKey: 'gift' }
  ],
  H: [
    { word: 'Hat', icon: '🎩', audioKey: 'hat' },
    { word: 'Horse', icon: '🐴', audioKey: 'horse' },
    { word: 'House', icon: '🏠', audioKey: 'house' },
    { word: 'Hand', icon: '✋', audioKey: 'hand' },
    { word: 'Honey', icon: '🍯', audioKey: 'honey' }
  ],
  I: [
    { word: 'Ice Cream', icon: '🍦', audioKey: 'ice_cream' },
    { word: 'Igloo', icon: '🛖', audioKey: 'igloo' },
    { word: 'Ink', icon: '✒️', audioKey: 'ink' },
    { word: 'Insect', icon: '🪲', audioKey: 'insect' },
    { word: 'Island', icon: '🏝️', audioKey: 'island' }
  ],
  J: [
    { word: 'Juice', icon: '🥤', audioKey: 'juice' },
    { word: 'Jam', icon: '🫙', audioKey: 'jam' },
    { word: 'Jacket', icon: '🧥', audioKey: 'jacket' },
    { word: 'Jar', icon: '🫙', audioKey: 'jar' },
    { word: 'Jeep', icon: '🚙', audioKey: 'jeep' }
  ],
  K: [
    { word: 'Kite', icon: '🪁', audioKey: 'kite' },
    { word: 'Key', icon: '🔑', audioKey: 'key' },
    { word: 'King', icon: '👑', audioKey: 'king' },
    { word: 'Kangaroo', icon: '🦘', audioKey: 'kangaroo' },
    { word: 'Kettle', icon: '🫖', audioKey: 'kettle' }
  ],
  L: [
    { word: 'Lion', icon: '🦁', audioKey: 'lion' },
    { word: 'Lemon', icon: '🍋', audioKey: 'lemon' },
    { word: 'Lamp', icon: '💡', audioKey: 'lamp' },
    { word: 'Leaf', icon: '🍃', audioKey: 'leaf' },
    { word: 'Ladder', icon: '🪜', audioKey: 'ladder' }
  ],
  M: [
    { word: 'Moon', icon: '🌙', audioKey: 'moon' },
    { word: 'Mango', icon: '🥭', audioKey: 'mango' },
    { word: 'Monkey', icon: '🐒', audioKey: 'monkey' },
    { word: 'Milk', icon: '🥛', audioKey: 'milk' },
    { word: 'Mouse', icon: '🐭', audioKey: 'mouse' }
  ],
  N: [
    { word: 'Nest', icon: '🪹', audioKey: 'nest' },
    { word: 'Nose', icon: '👃', audioKey: 'nose' },
    { word: 'Net', icon: '🥅', audioKey: 'net' },
    { word: 'Nurse', icon: '🧑‍⚕️', audioKey: 'nurse' },
    { word: 'Notebook', icon: '📓', audioKey: 'notebook' }
  ],
  O: [
    { word: 'Orange', icon: '🍊', audioKey: 'orange' },
    { word: 'Owl', icon: '🦉', audioKey: 'owl' },
    { word: 'Ox', icon: '🐂', audioKey: 'ox' },
    { word: 'Onion', icon: '🧅', audioKey: 'onion' },
    { word: 'Ocean', icon: '🌊', audioKey: 'ocean' }
  ],
  P: [
    { word: 'Parrot', icon: '🦜', audioKey: 'parrot' },
    { word: 'Pencil', icon: '✏️', audioKey: 'pencil' },
    { word: 'Pizza', icon: '🍕', audioKey: 'pizza' },
    { word: 'Pumpkin', icon: '🎃', audioKey: 'pumpkin' },
    { word: 'Panda', icon: '🐼', audioKey: 'panda' }
  ],
  Q: [
    { word: 'Question Mark', icon: '❓', audioKey: 'question_mark' },
    { word: 'Quack', icon: '🦆', audioKey: 'quack' },
    { word: 'Queue', icon: '📚', audioKey: 'queue' },
    { word: 'Queen', icon: '👸', audioKey: 'queen' },
    { word: 'Quail', icon: '🐦', audioKey: 'quail' }
  ],
  R: [
    { word: 'Rabbit', icon: '🐰', audioKey: 'rabbit' },
    { word: 'Rose', icon: '🌹', audioKey: 'rose' },
    { word: 'Ring', icon: '💍', audioKey: 'ring' },
    { word: 'Robot', icon: '🤖', audioKey: 'robot' },
    { word: 'Rainbow', icon: '🌈', audioKey: 'rainbow' }
  ],
  S: [
    { word: 'Sun', icon: '☀️', audioKey: 'sun' },
    { word: 'Star', icon: '⭐', audioKey: 'star' },
    { word: 'Snake', icon: '🐍', audioKey: 'snake' },
    { word: 'Shoe', icon: '👟', audioKey: 'shoe' },
    { word: 'Spoon', icon: '🥄', audioKey: 'spoon' }
  ],
  T: [
    { word: 'Tiger', icon: '🐯', audioKey: 'tiger' },
    { word: 'Tomato', icon: '🍅', audioKey: 'tomato' },
    { word: 'Table', icon: '🪵', audioKey: 'table' },
    { word: 'Tree', icon: '🌳', audioKey: 'tree' },
    { word: 'Train', icon: '🚂', audioKey: 'train' }
  ],
  U: [
    { word: 'Umbrella', icon: '☂️', audioKey: 'umbrella' },
    { word: 'Uniform', icon: '🥋', audioKey: 'uniform' },
    { word: 'Uncle', icon: '👨', audioKey: 'uncle' },
    { word: 'Urn', icon: '⚱️', audioKey: 'urn' },
    { word: 'Utensil', icon: '🍴', audioKey: 'utensil' }
  ],
  V: [
    { word: 'Van', icon: '🚐', audioKey: 'van' },
    { word: 'Vase', icon: '🏺', audioKey: 'vase' },
    { word: 'Violin', icon: '🎻', audioKey: 'violin' },
    { word: 'Vegetables', icon: '🥦', audioKey: 'vegetables' },
    { word: 'Vest', icon: '🎽', audioKey: 'vest' }
  ],
  W: [
    { word: 'Whale', icon: '🐋', audioKey: 'whale' },
    { word: 'Watch', icon: '⌚', audioKey: 'watch' },
    { word: 'Water', icon: '💧', audioKey: 'water' },
    { word: 'Window', icon: '🪟', audioKey: 'window' },
    { word: 'Watermelon', icon: '🍉', audioKey: 'watermelon' }
  ],
  X: [
    { word: 'Xylophone', icon: '🎼', audioKey: 'xylophone' },
    { word: 'X-ray', icon: '🩻', audioKey: 'xray' },
    { word: 'Xmas Tree', icon: '🎄', audioKey: 'xmas_tree' },
    { word: 'Fox', icon: '🦊', audioKey: 'fox' },
    { word: 'Box', icon: '📦', audioKey: 'box' }
  ],
  Y: [
    { word: 'Yacht', icon: '⛵', audioKey: 'yacht' },
    { word: 'Yellow', icon: '💛', audioKey: 'yellow' },
    { word: 'Yo-yo', icon: '🪀', audioKey: 'yoyo' },
    { word: 'Yak', icon: '🐂', audioKey: 'yak' },
    { word: 'Yogurt', icon: '🥣', audioKey: 'yogurt' }
  ],
  Z: [
    { word: 'Zebra', icon: '🦓', audioKey: 'zebra' },
    { word: 'Zoo', icon: '🦁', audioKey: 'zoo' },
    { word: 'Zip', icon: '🤐', audioKey: 'zip' },
    { word: 'Zero', icon: '0️⃣', audioKey: 'zero' },
    { word: 'Zigzag', icon: '📈', audioKey: 'zigzag' }
  ]
};

export const ENGLISH_WORDS: WordItem[] = [
  // --- ANIMALS (30 words) ---
  { id: 'en_anim_1', emoji: '🐱', word: 'Cat', phonetic: 'Cat', category: 'animals' },
  { id: 'en_anim_2', emoji: '🐶', word: 'Dog', phonetic: 'Dog', category: 'animals' },
  { id: 'en_anim_3', emoji: '🐮', word: 'Cow', phonetic: 'Cow', category: 'animals' },
  { id: 'en_anim_4', emoji: '🐐', word: 'Goat', phonetic: 'Goat', category: 'animals' },
  { id: 'en_anim_5', emoji: '🐑', word: 'Sheep', phonetic: 'Sheep', category: 'animals' },
  { id: 'en_anim_6', emoji: '🐴', word: 'Horse', phonetic: 'Horse', category: 'animals' },
  { id: 'en_anim_7', emoji: '🦁', word: 'Lion', phonetic: 'Lion', category: 'animals' },
  { id: 'en_anim_8', emoji: '🐯', word: 'Tiger', phonetic: 'Tiger', category: 'animals' },
  { id: 'en_anim_9', emoji: '🐘', word: 'Elephant', phonetic: 'Elephant', category: 'animals' },
  { id: 'en_anim_10', emoji: '🐒', word: 'Monkey', phonetic: 'Monkey', category: 'animals' },
  { id: 'en_anim_11', emoji: '🐰', word: 'Rabbit', phonetic: 'Rabbit', category: 'animals' },
  { id: 'en_anim_12', emoji: '🐻', word: 'Bear', phonetic: 'Bear', category: 'animals' },
  { id: 'en_anim_13', emoji: '🦌', word: 'Deer', phonetic: 'Deer', category: 'animals' },
  { id: 'en_anim_14', emoji: '🦊', word: 'Fox', phonetic: 'Fox', category: 'animals' },
  { id: 'en_anim_15', emoji: '🦓', word: 'Zebra', phonetic: 'Zebra', category: 'animals' },
  { id: 'en_anim_16', emoji: '🦒', word: 'Giraffe', phonetic: 'Giraffe', category: 'animals' },
  { id: 'en_anim_17', emoji: '🐪', word: 'Camel', phonetic: 'Camel', category: 'animals' },
  { id: 'en_anim_18', emoji: '🫏', word: 'Donkey', phonetic: 'Donkey', category: 'animals' },
  { id: 'en_anim_19', emoji: '🦆', word: 'Duck', phonetic: 'Duck', category: 'animals' },
  { id: 'en_anim_20', emoji: '🐔', word: 'Hen', phonetic: 'Hen', category: 'animals' },
  { id: 'en_anim_21', emoji: '🐓', word: 'Rooster', phonetic: 'Rooster', category: 'animals' },
  { id: 'en_anim_22', emoji: '🐟', word: 'Fish', phonetic: 'Fish', category: 'animals' },
  { id: 'en_anim_23', emoji: '🐬', word: 'Dolphin', phonetic: 'Dolphin', category: 'animals' },
  { id: 'en_anim_24', emoji: '🐋', word: 'Whale', phonetic: 'Whale', category: 'animals' },
  { id: 'en_anim_25', emoji: '🐸', word: 'Frog', phonetic: 'Frog', category: 'animals' },
  { id: 'en_anim_26', emoji: '🐢', word: 'Turtle', phonetic: 'Turtle', category: 'animals' },
  { id: 'en_anim_27', emoji: '🐍', word: 'Snake', phonetic: 'Snake', category: 'animals' },
  { id: 'en_anim_28', emoji: '🐦', word: 'Bird', phonetic: 'Bird', category: 'animals' },
  { id: 'en_anim_29', emoji: '🦜', word: 'Parrot', phonetic: 'Parrot', category: 'animals' },
  { id: 'en_anim_30', emoji: '🦉', word: 'Owl', phonetic: 'Owl', category: 'animals' },

  // --- FRUITS (20 words) ---
  { id: 'en_fruit_1', emoji: '🍎', word: 'Apple', phonetic: 'Apple', category: 'fruits' },
  { id: 'en_fruit_2', emoji: '🍌', word: 'Banana', phonetic: 'Banana', category: 'fruits' },
  { id: 'en_fruit_3', emoji: '🍊', word: 'Orange', phonetic: 'Orange', category: 'fruits' },
  { id: 'en_fruit_4', emoji: '🥭', word: 'Mango', phonetic: 'Mango', category: 'fruits' },
  { id: 'en_fruit_5', emoji: '🍇', word: 'Grapes', phonetic: 'Grapes', category: 'fruits' },
  { id: 'en_fruit_6', emoji: '🍓', word: 'Strawberry', phonetic: 'Strawberry', category: 'fruits' },
  { id: 'en_fruit_7', emoji: '🍉', word: 'Watermelon', phonetic: 'Watermelon', category: 'fruits' },
  { id: 'en_fruit_8', emoji: '🍍', word: 'Pineapple', phonetic: 'Pineapple', category: 'fruits' },
  { id: 'en_fruit_9', emoji: '🍑', word: 'Peach', phonetic: 'Peach', category: 'fruits' },
  { id: 'en_fruit_10', emoji: '🍐', word: 'Pear', phonetic: 'Pear', category: 'fruits' },
  { id: 'en_fruit_11', emoji: '🍒', word: 'Cherry', phonetic: 'Cherry', category: 'fruits' },
  { id: 'en_fruit_12', emoji: '🍋', word: 'Lemon', phonetic: 'Lemon', category: 'fruits' },
  { id: 'en_fruit_13', emoji: '🥥', word: 'Coconut', phonetic: 'Coconut', category: 'fruits' },
  { id: 'en_fruit_14', emoji: '🍎', word: 'Pomegranate', phonetic: 'Pomegranate', category: 'fruits' },
  { id: 'en_fruit_15', emoji: '🥭', word: 'Papaya', phonetic: 'Papaya', category: 'fruits' },
  { id: 'en_fruit_16', emoji: '🍈', word: 'Melon', phonetic: 'Melon', category: 'fruits' },
  { id: 'en_fruit_17', emoji: '🍑', word: 'Plum', phonetic: 'Plum', category: 'fruits' },
  { id: 'en_fruit_18', emoji: '🥝', word: 'Kiwi', phonetic: 'Kiwi', category: 'fruits' },
  { id: 'en_fruit_19', emoji: '🫒', word: 'Fig', phonetic: 'Fig', category: 'fruits' },
  { id: 'en_fruit_20', emoji: '🌴', word: 'Dates', phonetic: 'Dates', category: 'fruits' },

  // --- NATURE (25 words) ---
  { id: 'en_nat_1', emoji: '☀️', word: 'Sun', phonetic: 'Sun', category: 'nature' },
  { id: 'en_nat_2', emoji: '🌙', word: 'Moon', phonetic: 'Moon', category: 'nature' },
  { id: 'en_nat_3', emoji: '⭐', word: 'Star', phonetic: 'Star', category: 'nature' },
  { id: 'en_nat_4', emoji: '☁️', word: 'Cloud', phonetic: 'Cloud', category: 'nature' },
  { id: 'en_nat_5', emoji: '🌧️', word: 'Rain', phonetic: 'Rain', category: 'nature' },
  { id: 'en_nat_6', emoji: '❄️', word: 'Snow', phonetic: 'Snow', category: 'nature' },
  { id: 'en_nat_7', emoji: '💨', word: 'Wind', phonetic: 'Wind', category: 'nature' },
  { id: 'en_nat_8', emoji: '🌳', word: 'Tree', phonetic: 'Tree', category: 'nature' },
  { id: 'en_nat_9', emoji: '🍃', word: 'Leaf', phonetic: 'Leaf', category: 'nature' },
  { id: 'en_nat_10', emoji: '🌸', word: 'Flower', phonetic: 'Flower', category: 'nature' },
  { id: 'en_nat_11', emoji: '🌱', word: 'Grass', phonetic: 'Grass', category: 'nature' },
  { id: 'en_nat_12', emoji: '🏔️', word: 'Mountain', phonetic: 'Mountain', category: 'nature' },
  { id: 'en_nat_13', emoji: '🏞️', word: 'River', phonetic: 'River', category: 'nature' },
  { id: 'en_nat_14', emoji: '🌊', word: 'Sea', phonetic: 'Sea', category: 'nature' },
  { id: 'en_nat_15', emoji: '🌊', word: 'Ocean', phonetic: 'Ocean', category: 'nature' },
  { id: 'en_nat_16', emoji: '🪨', word: 'Stone', phonetic: 'Stone', category: 'nature' },
  { id: 'en_nat_17', emoji: '🏜️', word: 'Sand', phonetic: 'Sand', category: 'nature' },
  { id: 'en_nat_18', emoji: '🔥', word: 'Fire', phonetic: 'Fire', category: 'nature' },
  { id: 'en_nat_19', emoji: '💧', word: 'Water', phonetic: 'Water', category: 'nature' },
  { id: 'en_nat_20', emoji: '🌈', word: 'Rainbow', phonetic: 'Rainbow', category: 'nature' },
  { id: 'en_nat_21', emoji: '🌲', word: 'Forest', phonetic: 'Forest', category: 'nature' },
  { id: 'en_nat_22', emoji: '🏡', word: 'Garden', phonetic: 'Garden', category: 'nature' },
  { id: 'en_nat_23', emoji: '🌤️', word: 'Sky', phonetic: 'Sky', category: 'nature' },
  { id: 'en_nat_24', emoji: '🏜️', word: 'Desert', phonetic: 'Desert', category: 'nature' },
  { id: 'en_nat_25', emoji: '🏝️', word: 'Island', phonetic: 'Island', category: 'nature' },

  // --- VEHICLES (19 words) ---
  { id: 'en_veh_1', emoji: '🚗', word: 'Car', phonetic: 'Car', category: 'vehicles' },
  { id: 'en_veh_2', emoji: '🚌', word: 'Bus', phonetic: 'Bus', category: 'vehicles' },
  { id: 'en_veh_3', emoji: '🚐', word: 'Van', phonetic: 'Van', category: 'vehicles' },
  { id: 'en_veh_4', emoji: '🚚', word: 'Truck', phonetic: 'Truck', category: 'vehicles' },
  { id: 'en_veh_5', emoji: '🚂', word: 'Train', phonetic: 'Train', category: 'vehicles' },
  { id: 'en_veh_6', emoji: '✈️', word: 'Airplane', phonetic: 'Airplane', category: 'vehicles' },
  { id: 'en_veh_7', emoji: '🚁', word: 'Helicopter', phonetic: 'Helicopter', category: 'vehicles' },
  { id: 'en_veh_8', emoji: '🚲', word: 'Bicycle', phonetic: 'Bicycle', category: 'vehicles' },
  { id: 'en_veh_9', emoji: '🏍️', word: 'Motorcycle', phonetic: 'Motorcycle', category: 'vehicles' },
  { id: 'en_veh_10', emoji: '⛵', word: 'Boat', phonetic: 'Boat', category: 'vehicles' },
  { id: 'en_veh_11', emoji: '🚢', word: 'Ship', phonetic: 'Ship', category: 'vehicles' },
  { id: 'en_veh_12', emoji: '🛴', word: 'Scooter', phonetic: 'Scooter', category: 'vehicles' },
  { id: 'en_veh_13', emoji: '🚑', word: 'Ambulance', phonetic: 'Ambulance', category: 'vehicles' },
  { id: 'en_veh_14', emoji: '🚒', word: 'Fire Truck', phonetic: 'Fire Truck', category: 'vehicles' },
  { id: 'en_veh_15', emoji: '🚜', word: 'Tractor', phonetic: 'Tractor', category: 'vehicles' },
  { id: 'en_veh_16', emoji: '🚖', word: 'Taxi', phonetic: 'Taxi', category: 'vehicles' },
  { id: 'en_veh_17', emoji: '🚀', word: 'Rocket', phonetic: 'Rocket', category: 'vehicles' },
  { id: 'en_veh_18', emoji: '🚓', word: 'Police Car', phonetic: 'Police Car', category: 'vehicles' },
  { id: 'en_veh_19', emoji: '🚌', word: 'School Bus', phonetic: 'School Bus', category: 'vehicles' },

  // --- TOYS / OBJECTS (19 words) ---
  { id: 'en_toy_1', emoji: '⚽', word: 'Ball', phonetic: 'Ball', category: 'objects' },
  { id: 'en_toy_2', emoji: '🪆', word: 'Doll', phonetic: 'Doll', category: 'objects' },
  { id: 'en_toy_3', emoji: '🧸', word: 'Teddy Bear', phonetic: 'Teddy Bear', category: 'objects' },
  { id: 'en_toy_4', emoji: '🪁', word: 'Kite', phonetic: 'Kite', category: 'objects' },
  { id: 'en_toy_5', emoji: '🧱', word: 'Blocks', phonetic: 'Blocks', category: 'objects' },
  { id: 'en_toy_6', emoji: '🧩', word: 'Puzzle', phonetic: 'Puzzle', category: 'objects' },
  { id: 'en_toy_7', emoji: '🤖', word: 'Robot', phonetic: 'Robot', category: 'objects' },
  { id: 'en_toy_8', emoji: '🪀', word: 'Yo-yo', phonetic: 'Yo-yo', category: 'objects' },
  { id: 'en_toy_9', emoji: '🎈', word: 'Balloon', phonetic: 'Balloon', category: 'objects' },
  { id: 'en_toy_10', emoji: '🥁', word: 'Drum', phonetic: 'Drum', category: 'objects' },
  { id: 'en_toy_11', emoji: '🏎️', word: 'Toy Car', phonetic: 'Toy Car', category: 'objects' },
  { id: 'en_toy_12', emoji: '🚊', word: 'Toy Train', phonetic: 'Toy Train', category: 'objects' },
  { id: 'en_toy_13', emoji: '✈️', word: 'Toy Plane', phonetic: 'Toy Plane', category: 'objects' },
  { id: 'en_toy_14', emoji: '🪀', word: 'Spinning Top', phonetic: 'Spinning Top', category: 'objects' },
  { id: 'en_toy_15', emoji: '🪇', word: 'Rattle', phonetic: 'Rattle', category: 'objects' },
  { id: 'en_toy_16', emoji: '🧸', word: 'Teddy', phonetic: 'Teddy', category: 'objects' },
  { id: 'en_toy_17', emoji: '🔫', word: 'Water Gun', phonetic: 'Water Gun', category: 'objects' },
  { id: 'en_toy_18', emoji: '🚚', word: 'Toy Truck', phonetic: 'Toy Truck', category: 'objects' },
  { id: 'en_toy_19', emoji: '🧱', word: 'Building Blocks', phonetic: 'Building Blocks', category: 'objects' }
];
